import { useEffect, useId, useMemo, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "@tanstack/react-query";
import { alpha } from "@mui/material/styles";
import { PostDeploymentInstructionsView } from "../../components/PostDeploymentInstructionsView";
import { TabPanel } from "../../components/TabPanel";
import config from "../../data/config";
import { getDeployment, getDeploymentTemplate } from "../../services/pdmApi.service";
import type { DeploymentDetails } from "../../types/pdm.types";
import { fetchProviderExplorerDestinationUrl } from "../../utils/explorerLinks";

export function DeploymentDetailsDialog(props: {
  open: boolean;
  deploymentId: string | null;
  onClose: () => void;
}) {
  const { open, deploymentId, onClose } = props;

  const idPrefix = useId();
  const tabDetailsId = `${idPrefix}-deployment-details-tab`;
  const panelDetailsId = `${idPrefix}-deployment-details-panel`;
  const tabPostId = `${idPrefix}-post-instructions-tab`;
  const panelPostId = `${idPrefix}-post-instructions-panel`;

  const [tab, setTab] = useState(0);

  const detailsQuery = useQuery({
    queryKey: ["deploymentDetails", deploymentId],
    queryFn: () => getDeployment(deploymentId as string),
    enabled: open && Boolean(deploymentId),
  });

  const deployment = detailsQuery.data ?? null;

  const templateQuery = useQuery({
    queryKey: ["deploymentTemplate", deployment?.deploymentTemplateId],
    queryFn: () => getDeploymentTemplate(deployment!.deploymentTemplateId),
    enabled:
      open && detailsQuery.isSuccess && Boolean(deployment?.deploymentTemplateId),
  });

  const postInstructionsRaw = templateQuery.data?.postDeploymentInstructions;
  const postInstructions = Array.isArray(postInstructionsRaw)
    ? postInstructionsRaw
    : undefined;
  const showPostTab =
    templateQuery.isSuccess &&
    postInstructionsRaw !== undefined &&
    postInstructionsRaw !== null &&
    Boolean(postInstructions?.length);

  useEffect(() => {
    setTab(0);
  }, [deploymentId, open]);

  useEffect(() => {
    if (tab === 1 && !showPostTab) {
      setTab(0);
    }
  }, [tab, showPostTab]);

  const formattedCommitDate = useMemo(() => {
    if (!deployment?.commitDate) return "—";
    const d = new Date(deployment.commitDate);
    if (Number.isNaN(d.getTime())) return deployment.commitDate;
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  }, [deployment?.commitDate]);

  const createdAt = useMemo(() => {
    if (!deployment?.createdAt) return "—";
    const d = new Date(deployment.createdAt);
    if (Number.isNaN(d.getTime())) return deployment.createdAt;
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  }, [deployment?.createdAt]);

  const startedAt = useMemo(() => formatMaybeDate(deployment?.startedAt), [deployment?.startedAt]);
  const completedAt = useMemo(
    () => formatMaybeDate(deployment?.completedAt),
    [deployment?.completedAt],
  );

  const updatedAtLabel = useMemo(() => {
    const raw = deployment?.updatedAt ?? deployment?.updateAt ?? null;
    return formatMaybeDate(raw);
  }, [deployment?.updatedAt, deployment?.updateAt]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <AppBar position="sticky" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Deployment Details
          </Typography>
          <IconButton aria-label="close" edge="end" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {detailsQuery.isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {detailsQuery.isError && (
          <Stack spacing={2} sx={{ maxWidth: 960, mx: "auto" }}>
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void detailsQuery.refetch()}
                >
                  Retry
                </Button>
              }
            >
              Unable to load deployment details.
            </Alert>
          </Stack>
        )}

        {deployment && !detailsQuery.isLoading && !detailsQuery.isError && (
          <Stack spacing={2} sx={{ maxWidth: 1100, mx: "auto" }}>
            <Box
              aria-busy={templateQuery.isLoading}
              sx={{
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Tabs
                id={`${idPrefix}-deployment-section-tabs`}
                value={tab}
                onChange={(_event, newValue) => {
                  setTab(typeof newValue === "number" ? newValue : 0);
                }}
                aria-label="Deployment details sections"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ minHeight: 48 }}
              >
                <Tab
                  id={tabDetailsId}
                  aria-controls={panelDetailsId}
                  label="Deployment Details"
                />
                {showPostTab ? (
                  <Tab
                    id={tabPostId}
                    aria-controls={panelPostId}
                    label="Post Deployment Instructions"
                  />
                ) : null}
              </Tabs>
              <Box
                sx={{
                  height: 4,
                  position: "relative",
                  visibility: templateQuery.isLoading ? "visible" : "hidden",
                }}
                aria-hidden={!templateQuery.isLoading}
              >
                {templateQuery.isLoading ? (
                  <LinearProgress
                    sx={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                    }}
                  />
                ) : null}
              </Box>
            </Box>

            {templateQuery.isError ? (
              <Alert
                severity="warning"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => void templateQuery.refetch()}
                  >
                    Retry
                  </Button>
                }
              >
                Could not load deployment template. Post-deployment instructions are
                unavailable.
              </Alert>
            ) : null}

            <TabPanel
              value={tab}
              index={0}
              id={panelDetailsId}
              labelledBy={tabDetailsId}
            >
              <Stack spacing={2.5}>
                <Stack spacing={0.5}>
                  <Typography variant="h5" sx={{ wordBreak: "break-word" }}>
                    {deployment.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                    {deployment.id}
                  </Typography>
                </Stack>

                <Stack spacing={1}>
                  <Typography variant="subtitle1">Status</Typography>
                  <Box
                    sx={(theme) => {
                      const color = theme.palette[getStatusColorKey(deployment.status)].main;
                      return {
                        display: "inline-flex",
                        alignItems: "center",
                        width: "fit-content",
                        maxWidth: "100%",
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1,
                        bgcolor: alpha(color, 0.08),
                        border: `1px solid ${alpha(color, 0.6)}`,
                      };
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={(theme) => ({
                        color: theme.palette[getStatusColorKey(deployment.status)].main,
                        fontWeight: 600,
                        textTransform: "lowercase",
                        wordBreak: "break-word",
                      })}
                    >
                      {deployment.status}
                    </Typography>
                  </Box>
                </Stack>

                <Stack spacing={1.5}>
                  <Typography variant="subtitle1">Deployment Information</Typography>
                  <Divider />
                  <Grid container spacing={2}>
                    <Field label="Network" value={deployment.network} />
                    <Field label="Template ID" value={deployment.deploymentTemplateId} mono />
                    <Field label="Description" value={deployment.description} />
                    <ExplorerAddressField
                      label="Owner address"
                      address={deployment.ownerAddress}
                      baseUrl={config.PDM_API_BASE_URL}
                      provider={deployment.provider ?? deployment.network}
                    />
                    <Field label="Deployed by" value={deployment.deployedBy} mono />
                    <ExplorerAddressField
                      label="Address"
                      address={deployment.address}
                      baseUrl={config.PDM_API_BASE_URL}
                      provider={deployment.provider ?? deployment.network}
                    />
                    <Field label="Created at" value={createdAt} />
                    <Field label="Started at" value={startedAt} />
                    <Field label="Completed at" value={completedAt} />
                    <Field label="Updated at" value={updatedAtLabel} />
                    <ErrorMessageField errorMessage={deployment.errorMessage} />
                  </Grid>
                </Stack>

                <Stack spacing={1.5}>
                  <Typography variant="subtitle1">Parameters</Typography>
                  <Divider />
                  <Stack spacing={2}>
                    <JsonParamsBlock
                      label="Required parameters"
                      value={deployment.requiredParameters}
                    />
                    <JsonParamsBlock
                      label="Optional parameters"
                      value={deployment.optionalParameters}
                    />
                  </Stack>
                </Stack>

                <Stack spacing={1.5}>
                  <Typography variant="subtitle1">Repository Details</Typography>
                  <Divider />
                  <Grid container spacing={2}>
                    <Field label="Branch" value={deployment.branch} />
                    <Field label="Commit hash" value={deployment.commitHash} mono />
                    <Field label="Commit message" value={deployment.commitMessage} />
                    <Field label="Commit author" value={deployment.commitAuthor} />
                    <Field label="Commit date" value={formattedCommitDate} />
                  </Grid>
                </Stack>
              </Stack>
            </TabPanel>

            {showPostTab ? (
              <TabPanel
                value={tab}
                index={1}
                id={panelPostId}
                labelledBy={tabPostId}
              >
                {tab === 1 && postInstructions ? (
                  <PostDeploymentInstructionsView
                    instructions={postInstructions}
                    context={{
                      network: deployment?.network,
                      provider: deployment?.provider ?? deployment?.network,
                      deploymentAddress:
                        deployment?.address ||
                        (deployment
                          ? `Deployment in ${deployment.status} status`
                          : undefined),
                      deploymentAddressLinkAddress: deployment?.address,
                    }}
                  />
                ) : null}
              </TabPanel>
            ) : null}
          </Stack>
        )}
      </Box>
    </Dialog>
  );
}

function ExplorerAddressField(props: {
  label: string;
  address: string;
  baseUrl: string;
  provider?: string;
}) {
  const { label, address, baseUrl, provider } = props;
  const trimmedBase = baseUrl.trim();
  const trimmedProvider = provider?.trim() ?? "";
  const trimmedAddress = address?.trim() ?? "";
  const canFetch =
    Boolean(trimmedBase) && Boolean(trimmedProvider) && Boolean(trimmedAddress);

  const explorerUrlQuery = useQuery({
    queryKey: [
      "abstractionLayerExplorerUrl",
      trimmedBase,
      trimmedProvider,
      trimmedAddress,
    ],
    queryFn: () =>
      fetchProviderExplorerDestinationUrl(
        trimmedBase,
        trimmedProvider,
        trimmedAddress,
      ),
    enabled: canFetch,
    staleTime: 60 * 60 * 1000,
  });

  const href = explorerUrlQuery.data ?? undefined;

  return (
    <Grid item xs={12} md={6}>
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        {!canFetch ? (
          <Typography
            variant="body2"
            sx={{
              wordBreak: "break-all",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            }}
          >
            {address || "—"}
          </Typography>
        ) : explorerUrlQuery.isLoading ? (
          <Skeleton
            variant="rounded"
            width="100%"
            height={24}
            sx={{ maxWidth: 420 }}
            aria-label={`Loading ${label} explorer link`}
          />
        ) : href ? (
          <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            color="primary"
            underline="hover"
            aria-label={`${label}, opens blockchain explorer in a new window`}
            sx={{
              wordBreak: "break-all",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            }}
          >
            {address}
          </Link>
        ) : (
          <Typography
            variant="body2"
            sx={{
              wordBreak: "break-all",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            }}
          >
            {address || "—"}
          </Typography>
        )}
      </Stack>
    </Grid>
  );
}

function JsonParamsBlock(props: {
  label: string;
  value: DeploymentDetails["requiredParameters"];
}) {
  const { label, value } = props;
  const code = useMemo(() => JSON.stringify(value ?? {}, null, 2), [value]);

  return (
    <Stack spacing={0.75}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          overflow: "hidden",
          bgcolor: (t) =>
            t.palette.mode === "dark" ? "grey.900" : t.palette.action.hover,
        }}
      >
        <Box
          sx={{
            maxHeight: 320,
            overflow: "auto",
            tabIndex: 0,
          }}
        >
          <Box
            component="pre"
            sx={(theme) => ({
              m: 0,
              p: 2,
              fontSize: theme.typography.body2.fontSize,
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              color: "text.primary",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            })}
          >
            {code}
          </Box>
        </Box>
      </Paper>
    </Stack>
  );
}

function ErrorMessageField(props: { errorMessage?: string | null }) {
  const { errorMessage } = props;
  const raw = errorMessage ?? "";
  const hasContent = raw.trim().length > 0;

  return (
    <Grid item xs={12}>
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          Error message
        </Typography>
        {hasContent ? (
          <Paper
            variant="outlined"
            sx={{
              bgcolor: (t) =>
                t.palette.mode === "dark" ? "grey.900" : t.palette.action.hover,
            }}
          >
            <TextField
              multiline
              fullWidth
              value={raw}
              inputProps={{
                readOnly: true,
                spellCheck: false,
              }}
              variant="outlined"
              aria-label="Error message (read-only)"
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "transparent",
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  py: 2,
                  px: 2,
                  maxHeight: "min(70vh, 640px)",
                  overflowY: "auto",
                  overflowX: "auto",
                  cursor: "default",
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontSize: (theme) => theme.typography.body2.fontSize,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                },
              }}
            />
          </Paper>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Stack>
    </Grid>
  );
}

function Field(props: { label: string; value: string; mono?: boolean }) {
  const { label, value, mono } = props;
  return (
    <Grid item xs={12} md={6}>
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            wordBreak: mono ? "break-all" : "break-word",
            fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" : undefined,
          }}
        >
          {value || "—"}
        </Typography>
      </Stack>
    </Grid>
  );
}

function formatMaybeDate(value?: string | null) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function getStatusColorKey(
  status: DeploymentDetails["status"],
): "success" | "warning" | "info" | "error" {
  switch (status) {
    case "success":
      return "success";
    case "pending":
      return "warning";
    case "in_progress":
      return "info";
    case "failed":
      return "error";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

